<script setup lang="ts">
import { ref, onMounted, onBeforeUnmount, watch, nextTick } from 'vue'
import type { ChartDoughnut } from '../types'
import { spacingClasses } from '../utils/classes'

const props = defineProps<{ element: ChartDoughnut }>()

const canvasRefs = ref<HTMLCanvasElement[]>([])
const chartInstances: any[] = []

function loadChartJs(): Promise<void> {
  if ((window as any).Chart) return Promise.resolve()
  return new Promise((resolve, reject) => {
    const script = document.createElement('script')
    script.src = 'https://cdn.jsdelivr.net/npm/chart.js@4.4.1/dist/chart.umd.min.js'
    script.onload = () => resolve()
    script.onerror = reject
    document.head.appendChild(script)
  })
}

function toNumber(value: unknown): number {
  const numericValue = Number(value)
  return Number.isFinite(numericValue) ? numericValue : 0
}

function destroyCharts() {
  chartInstances.forEach((chartInstance) => chartInstance.destroy())
  chartInstances.length = 0
}

function prefersReducedMotion(): boolean {
  return window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false
}

async function renderCharts() {
  await loadChartJs()
  const Chart = (window as any).Chart
  destroyCharts()

  canvasRefs.value.forEach((canvas, i) => {
    const item = props.element.items[i]
    if (!canvas || !item) return

    const value = toNumber(item.value)
    const max = toNumber(item.max)
    const filled = Math.max(0, Math.min(value, max))
    const remaining = Math.max(0, max - filled)

    chartInstances.push(new Chart(canvas, {
      type: 'doughnut',
      data: {
        datasets: [{
          data: [filled, remaining],
          backgroundColor: [item.color ?? '#60a5fa', 'rgba(128,128,128,0.15)'],
          borderWidth: 0,
          borderRadius: 3,
        }],
      },
      options: {
        cutout: '72%',
        animation: prefersReducedMotion()
          ? false
          : {
              animateRotate: true,
              animateScale: true,
              duration: 700,
              easing: 'easeOutCubic',
              delay: i * 120,
            },
        plugins: {
          legend: { display: false },
          tooltip: { enabled: false },
        },
      },
    }))
  })
}

onMounted(async () => {
  await nextTick()
  await renderCharts()
})

watch(
  () => props.element.items,
  async () => {
    await nextTick()
    await renderCharts()
  },
  { deep: true },
)

onBeforeUnmount(() => {
  destroyCharts()
})
</script>

<template>
  <div :class="element.spacing ? spacingClasses[element.spacing] : 'mt-2'">
    <div v-if="element.title || element.subtitle" class="text-center mb-3">
      <span class="text-base font-bold">{{ element.title }}</span>
      <span v-if="element.subtitle" class="text-sm text-muted-foreground ml-1">/ {{ element.subtitle }}</span>
    </div>
    <div class="flex justify-center gap-6">
      <div v-for="(item, i) in element.items" :key="i" class="flex flex-col items-center gap-1">
        <div class="relative w-20 h-20">
          <canvas
            :ref="(el) => { if (el) canvasRefs[i] = el as HTMLCanvasElement }"
            width="80"
            height="80"
          />
          <div class="absolute inset-0 flex items-center justify-center pointer-events-none">
            <span class="text-sm font-semibold leading-none">{{ item.value }}{{ item.unit }}</span>
          </div>
        </div>
        <span class="text-xs font-medium uppercase tracking-wide">{{ item.label }}</span>
        <span class="text-xs text-muted-foreground">{{ item.value }}/{{ item.max }}{{ item.unit }}</span>
      </div>
    </div>
  </div>
</template>
